<script setup>
// How much daylight a product lets through, drawn as five bands and named once:
// "Luz que pasa". The value is the most light it passes, so the meters are
// comparable across every card. A product that also regulates down to darkness
// (a duo, a double, slatted blinds) carries a small "Regulable" tag beside the
// reading instead of a second, competing caption — one property, one label,
// with the adjustable ones flagged. Two captions ("luz que pasa" vs "luz
// regulable") read as two different scales and confused people; this is one.
defineProps({
  value: { type: Number, required: true },
  total: { type: Number, default: 5 },
  // dark: on dark backgrounds. light: on sand and linen.
  surface: { type: String, default: 'dark' },
  // true when the product can be lowered from `value` down to darkness.
  adjustable: { type: Boolean, default: false }
})
</script>

<template>
  <!-- Side by side wherever it fits. In the two-up phone grid the card is 163px,
       so the caption drops under the bars instead of crowding the row. -->
  <div class="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-3">
    <div
      class="flex items-end gap-1"
      role="img"
      :aria-label="adjustable
        ? `Luz que pasa: hasta ${value} de ${total}, regulable a oscuridad`
        : `Luz que pasa: ${value} de ${total}`"
    >
      <span
        v-for="band in total"
        :key="band"
        class="block w-1 rounded-full transition-all duration-500"
        :class="[
          band <= value
            ? 'bg-glow'
            : surface === 'dark' ? 'bg-linen/25' : 'bg-ink/20',
          band <= value ? 'h-5' : 'h-3'
        ]"
      ></span>
    </div>

    <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span
        class="eyebrow"
        :class="surface === 'dark' ? 'text-linen/65' : 'text-ink-soft'"
      >
        Luz que pasa
      </span>

      <!-- El único diferenciador entre una tela fija y una que se gradúa: una
           etiqueta, no otro medidor con otro nombre. -->
      <span
        v-if="adjustable"
        class="eyebrow rounded-full px-2 py-0.5 text-[0.65rem] leading-normal ring-1 ring-inset"
        :class="surface === 'dark'
          ? 'text-linen/75 ring-linen/25'
          : 'text-ink-soft ring-ink/20'"
      >
        Regulable
      </span>
    </div>
  </div>
</template>
